import { memo, useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { Drawer, Tag, Table, Spin, Empty, Typography, Badge } from 'antd';
import {
  FileTextOutlined,
  CheckCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { CollectFormAPIs, CollectForm_GetOne_Response } from '~/common/typescript/apis/CollectFormAPIs';
import { formatDateTime } from '~/utils/dateUtils';
import UUIDViewer from '~/components/common/UUIDViewer';
import { getFieldTypeMeta } from '../../panels/GeocollectDatatablePanel/utils/FieldTypeIcon';

const { Text, Paragraph } = Typography;

export interface CollectFormDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  formUuid?: string;
  formData?: CollectForm_GetOne_Response | null;
  loading?: boolean;
}

const CollectFormDetailDrawer = ({
  open,
  onClose,
  formUuid,
  formData: initialFormData,
  loading: initialLoading,
}: CollectFormDetailDrawerProps) => {
  const [formData, setFormData] = useState<CollectForm_GetOne_Response | null>(initialFormData || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialFormData) {
      setFormData(initialFormData);
    }
  }, [initialFormData]);

  useEffect(() => {
    if (open && !initialFormData && formUuid) {
      setLoading(true);
      CollectFormAPIs.remote_getOne(formUuid)
        .then((res) => {
          setFormData(res);
        })
        .catch((err) => {
          console.error('Error fetching collect form detail:', err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, formUuid, initialFormData]);

  // Parse fields from content
  const fields = useMemo(() => {
    if (!formData?.content) return [];
    let content: any = formData.content;
    if (typeof content === 'string') {
      try {
        content = JSON.parse(content);
      } catch (err) {
        console.warn('Failed to parse form content:', err);
        return [];
      }
    }

    if (Array.isArray(content)) {
      return content;
    }
    if (content && typeof content === 'object' && Array.isArray(content.fields)) {
      return content.fields;
    }
    return [];
  }, [formData?.content]);

  const columns = [
    {
      title: 'Tên trường',
      dataIndex: 'label',
      key: 'label',
      render: (label: string, record: any) => (
        <div>
          <UI_FieldLabel>{label || record.key || 'Chưa đặt tên'}</UI_FieldLabel>
          <UI_FieldKey>{record.key}</UI_FieldKey>
        </div>
      ),
    },
    {
      title: 'Kiểu dữ liệu',
      dataIndex: 'type',
      key: 'type',
      width: 140,
      render: (type: string, record: any) => {
        const meta = getFieldTypeMeta(type, record.key);
        const Icon = meta.IconComponent;
        return (
          <Tag color={meta.tagColor} icon={<Icon style={{ marginRight: 4 }} />}>
            {meta.normalizedType}
          </Tag>
        );
      },
    },
    {
      title: 'Bắt buộc',
      dataIndex: 'required',
      key: 'required',
      width: 90,
      align: 'center' as const,
      render: (required: boolean) => (
        required ? (
          <Tag color="red" icon={<CheckCircleOutlined />}>Có</Tag>
        ) : (
          <Tag color="default" icon={<MinusCircleOutlined />}>Không</Tag>
        )
      ),
    },
  ];

  const isLoading = loading || initialLoading;

  return (
    <Drawer
      title={
        <UI_DrawerHeader>
          <FileTextOutlined style={{ color: '#2563eb', fontSize: 18 }} />
          <span>Thông tin biểu mẫu</span>
        </UI_DrawerHeader>
      }
      placement="right"
      width={500}
      open={open}
      onClose={onClose}
      destroyOnClose={false}
      styles={{
        body: { padding: '20px' },
      }}
    >
      {isLoading ? (
        <UI_LoadingBox>
          <Spin size="large" />
          <Text type="secondary" style={{ marginTop: 12 }}>Đang tải thông tin biểu mẫu...</Text>
        </UI_LoadingBox>
      ) : formData ? (
        <UI_ContentWrapper>
          {/* Header Card */}
          <UI_FormCard>
            <UI_FormTitle>{formData.name || 'Biểu mẫu không tên'}</UI_FormTitle>
            {formData.description ? (
              <Paragraph type="secondary" style={{ marginBottom: 12 }}>
                {formData.description}
              </Paragraph>
            ) : (
              <Paragraph type="secondary" style={{ marginBottom: 12, fontStyle: 'italic' }}>
                Không có mô tả
              </Paragraph>
            )}

            <UI_MetaGrid>
              <UI_MetaItem>
                <UI_MetaLabel>Mã UUID:</UI_MetaLabel>
                <UI_MetaValue>
                  <UUIDViewer uuid={formData.uuid} displayMode="short" />
                </UI_MetaValue>
              </UI_MetaItem>

              {formData.form_version !== undefined && (
                <UI_MetaItem>
                  <UI_MetaLabel>Phiên bản:</UI_MetaLabel>
                  <UI_MetaValue>
                    <Tag color="geekblue">V{formData.form_version}</Tag>
                  </UI_MetaValue>
                </UI_MetaItem>
              )}

              {formData.project_id && (
                <UI_MetaItem>
                  <UI_MetaLabel>Dự án ID:</UI_MetaLabel>
                  <UI_MetaValue>#{formData.project_id}</UI_MetaValue>
                </UI_MetaItem>
              )}

              {formData.user && (
                <UI_MetaItem>
                  <UI_MetaLabel>Người tạo:</UI_MetaLabel>
                  <UI_MetaValue>
                    {formData.user.fullname || formData.user.username || 'Không rõ'}
                  </UI_MetaValue>
                </UI_MetaItem>
              )}

              {formData.created_at && (
                <UI_MetaItem>
                  <UI_MetaLabel>Ngày tạo:</UI_MetaLabel>
                  <UI_MetaValue>{formatDateTime(formData.created_at)}</UI_MetaValue>
                </UI_MetaItem>
              )}

              {formData.updated_at && (
                <UI_MetaItem>
                  <UI_MetaLabel>Cập nhật:</UI_MetaLabel>
                  <UI_MetaValue>{formatDateTime(formData.updated_at)}</UI_MetaValue>
                </UI_MetaItem>
              )}
            </UI_MetaGrid>
          </UI_FormCard>

          {/* Fields Section */}
          <UI_SectionHeader>
            <UI_SectionTitle>
              Danh sách trường thu thập
              <Badge
                count={fields.length}
                overflowCount={999}
                style={{ backgroundColor: '#2563eb', marginLeft: 8 }}
              />
            </UI_SectionTitle>
          </UI_SectionHeader>

          {fields.length > 0 ? (
            <Table
              dataSource={fields.map((f: any, idx: number) => ({ ...f, key: f.key || idx }))}
              columns={columns}
              pagination={false}
              size="small"
              bordered
              scroll={{ y: 350 }}
            />
          ) : (
            <Empty description="Biểu mẫu chưa có trường thu thập nào" />
          )}
        </UI_ContentWrapper>
      ) : (
        <Empty description="Không tìm thấy thông tin biểu mẫu" />
      )}
    </Drawer>
  );
};

export default memo(CollectFormDetailDrawer);

const UI_DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 16px;
  color: #1e293b;
`;

const UI_LoadingBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
`;

const UI_ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const UI_FormCard = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
`;

const UI_FormTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 6px;
`;

const UI_MetaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  padding-top: 10px;
  border-top: 1px solid #e2e8f0;
`;

const UI_MetaItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
`;

const UI_MetaLabel = styled.span`
  color: #64748b;
  font-weight: 500;
`;

const UI_MetaValue = styled.span`
  color: #1e293b;
  font-weight: 500;
  text-align: right;
`;

const UI_SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
`;

const UI_SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
`;

const UI_FieldLabel = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: #1e293b;
`;

const UI_FieldKey = styled.div`
  font-family: monospace;
  font-size: 11px;
  color: #64748b;
`;
